import type * as THREE from 'three/webgpu';
import type {CollectSource} from '../../mesh/sketch/ISketchObject';
import type {SketchPointObject} from '../../mesh/sketch/SketchPointObject';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../config/SketchGeometryToleranceConfig';
import {SketchClosureEndpointAdapter} from './SketchClosureEndpointAdapter';
import {
    SketchClosureVertexMatchKind,
    type SketchClosureHalfEdge,
    type SketchClosureTopologyGraph,
    type SketchClosureTopologyVertex,
} from './SketchClosureTypes';

//------ 草图闭合模块化 开始------
/**
 * 草图闭合拓扑构建器。
 *
 * 设计说明：
 * 1. 闭合判断必须基于同一草图平面的拓扑点，而不是单纯比较边数组顺序；
 * 2. 共享端点对象优先表示真实拓扑连接，坐标容差只作为修剪点、象限点等独立对象的兜底；
 * 3. 每条边生成正反两个半边，使后续闭合搜索可以像 SOLIDWORKS 一样按需要反向遍历草图边。
 */
export class SketchClosureTopologyBuilder {
    /** 闭合端点适配器。 */
    private readonly endpointAdapter: SketchClosureEndpointAdapter;

    /**
     * 构造拓扑构建器。
     *
     * @param endpointAdapter 闭合端点适配器。
     */
    public constructor(endpointAdapter: SketchClosureEndpointAdapter = new SketchClosureEndpointAdapter()) {
        this.endpointAdapter = endpointAdapter;
    }

    /**
     * 从集合源边构建闭合拓扑图。
     *
     * @param sources 集合源边列表。
     * @returns 闭合拓扑图。
     */
    public build(sources: readonly CollectSource[]): SketchClosureTopologyGraph {
        const referencePlane = this.resolveReferencePlane(sources);
        const vertices = new Map<string, SketchClosureTopologyVertex>();
        const pointVertexKeyMap = new Map<string, string>();
        const halfEdgesBySourceId = new Map<string, SketchClosureHalfEdge[]>();

        sources.forEach((source) => {
            const endpoints = this.endpointAdapter.getSourceEndpoints(source);
            const startKey = this.ensureVertex(
                endpoints.startPoint,
                referencePlane,
                vertices,
                pointVertexKeyMap,
            );
            const endKey = this.ensureVertex(
                endpoints.endPoint,
                referencePlane,
                vertices,
                pointVertexKeyMap,
            );
            const halfEdges = this.endpointAdapter.createTraversalStepsForSource(source).map((traversalStep) => ({
                source,
                traversalStep,
                startKey: traversalStep.reversed ? endKey : startKey,
                endKey: traversalStep.reversed ? startKey : endKey,
            }));
            halfEdgesBySourceId.set(source.uuid, halfEdges);
        });

        return {
            vertices,
            halfEdgesBySourceId,
        };
    }

    /**
     * 解析拓扑构建使用的参考面。
     *
     * @param sources 集合源边列表。
     * @returns 草图参考面。
     */
    private resolveReferencePlane(sources: readonly CollectSource[]): ReferencePlane {
        const firstSource = sources[0];
        if (!firstSource) {
            throw new Error('缺少草图边，无法构建闭合拓扑图。');
        }
        return firstSource.referencePlane;
    }

    /**
     * 确保控制点拥有对应拓扑顶点。
     *
     * @param point 控制点。
     * @param referencePlane 草图参考面。
     * @param vertices 顶点表。
     * @param pointVertexKeyMap 控制点到拓扑顶点键的索引。
     * @returns 拓扑顶点键。
     */
    private ensureVertex(
        point: SketchPointObject,
        referencePlane: ReferencePlane,
        vertices: Map<string, SketchClosureTopologyVertex>,
        pointVertexKeyMap: Map<string, string>,
    ): string {
        const existingPointVertexKey = pointVertexKeyMap.get(point.uuid);
        if (existingPointVertexKey) {
            const existingVertex = vertices.get(existingPointVertexKey);
            if (existingVertex && existingVertex.matchKind === SketchClosureVertexMatchKind.Created) {
                existingVertex.matchKind = SketchClosureVertexMatchKind.SharedPoint;
            }
            return existingPointVertexKey;
        }

        const worldPosition = point.getPosition();
        const localPosition = referencePlane.worldToLocal(worldPosition);
        const coordinateMatchedVertex = this.findCoordinateMatchedVertex(localPosition, vertices);
        if (coordinateMatchedVertex) {
            coordinateMatchedVertex.pointIds.add(point.uuid);
            pointVertexKeyMap.set(point.uuid, coordinateMatchedVertex.key);
            return coordinateMatchedVertex.key;
        }

        const vertexKey = `closure-v-${vertices.size + 1}`;
        vertices.set(vertexKey, {
            key: vertexKey,
            point,
            worldPosition,
            localPosition,
            pointIds: new Set([point.uuid]),
            matchKind: SketchClosureVertexMatchKind.Created,
        });
        pointVertexKeyMap.set(point.uuid, vertexKey);
        return vertexKey;
    }

    /**
     * 按草图平面坐标容差查找可归并的已有顶点。
     *
     * @param localPosition 当前端点局部坐标。
     * @param vertices 顶点表。
     * @returns 坐标重合的顶点；不存在时返回 null。
     */
    private findCoordinateMatchedVertex(
        localPosition: THREE.Vector2,
        vertices: Map<string, SketchClosureTopologyVertex>,
    ): SketchClosureTopologyVertex | null {
        const toleranceSquared = SKETCH_GEOMETRY_TOLERANCE_CONFIG.collectionClosurePosition ** 2;
        for (const vertex of vertices.values()) {
            if (vertex.localPosition.distanceToSquared(localPosition) <= toleranceSquared) {
                vertex.matchKind = SketchClosureVertexMatchKind.Coordinate;
                return vertex;
            }
        }
        return null;
    }
}
//------ 草图闭合模块化 结束------
